import * as ApplicationsActions from './applications.actions';

import {createReducer, on} from '@ngrx/store';
import {createEntityAdapter, EntityAdapter} from '@ngrx/entity';
import {Md5} from "ts-md5";
import dayjs from 'dayjs';
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {ApplicationModel, ApplicationsSearchParams, ApplicationsViewType, CategoryHash, OrderCategory, OrderHistory} from '../_common/models/order.models';
import {EntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';
import {TenHistory} from '../../../tenant-cabinet/_models/customer-service.models';
import {DahLoadingType} from '@common/components-module/core-components.models';
import * as CustomerServiceActions from '../../../tenant-cabinet/customer-service/_store/customer-service.actions';

export const applicationsFeatureKey = 'applications';

export function sortByDate(a: ApplicationModel, b: ApplicationModel) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'created');
}

export const adapter: EntityAdapter<ApplicationModel> = createEntityAdapter<ApplicationModel>({
    sortComparer: sortByDate
});

export function sortCommentsByDate(a: TenHistory, b: TenHistory) {
    return StoredComponentHelperService.sortArrayComparer(a, b, 'created');
}

export const commentsAdapter: EntityAdapter<TenHistory> = createEntityAdapter<TenHistory>({
    sortComparer: sortCommentsByDate
});

export interface ApplicationsState {
    BOARD: EntityStateModel<ApplicationModel, ApplicationsSearchParams>,
    LIST: EntityStateModel<ApplicationModel, ApplicationsSearchParams>,
    categoryHashes?: CategoryHash[],
    categories?: OrderCategory[],
    openedItem: {
        comments: EntityStateModel<OrderHistory, any>,
    }
}

export const typeInitialState: EntityStateModel<ApplicationModel, ApplicationsSearchParams> = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: []
});

export const commentsInitialState: EntityStateModel<OrderHistory, any> = commentsAdapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {size: 30},
    itemsLoading: [],
});

export const initialState: ApplicationsState = {
    BOARD: {...typeInitialState},
    LIST: {...typeInitialState},
    openedItem: {
        comments: {...commentsInitialState}
    }
};

export const applicationsReducer = createReducer(
    initialState,

    on(ApplicationsActions.SetCategoriesSuccess, (state, action) => ({
        ...state,
        categories: action.payload
    })),

    on(ApplicationsActions.SetCategoryHashesSuccess, (state, action) => ({
        ...state,
        categoryHashes: action.payload
    })),

    on(ApplicationsActions.LoadList, (state, action) => {
        return ({
            ...state,
            [action.payload.viewType]: {
                ...state[action.payload.viewType],
                listLoading: action.payload.loading
            }
        });
    }),

    on(ApplicationsActions.LoadListSuccess, (state, action) => {
        return ({
            ...state,
            [action.payload.viewType]: action.payload.clearState
                ? adapter.setAll(action.payload.items, {
                    ...state[action.payload.viewType],
                    pagination: action.payload.pagination,
                    listLoading: undefined

                })
                : adapter.upsertMany(action.payload.items, {
                    ...state[action.payload.viewType],
                    pagination: action.payload.pagination,
                    listLoading: undefined
                }),
        });
    }),

    on(ApplicationsActions.LoadListFailure, (state, action) => ({
        ...state,
        [action.payload.viewType]: {
            ...state[action.payload.viewType],
            listLoading: undefined
        }
    })),

    on(ApplicationsActions.SetPagination, (state, action) => ({
        ...state,
        [action.payload.viewType]: {
            ...state[action.payload.viewType],
            pagination: {
                ...state[action.payload.viewType].pagination,
                ...action.payload.viewType == 'LIST' && {
                    number: action.payload.next ? StoredComponentHelperService.setNextPage(state['LIST'].pagination, state['LIST'].ids?.length) : 0
                }
            }
        }
    })),

    on(ApplicationsActions.SetFilter, (state, action) => ({
        ...state,
        [action.payload.viewType]: {
            ...state[action.payload.viewType],
            filter: action.payload.filter
        }
    })),

    on(ApplicationsActions.SetLoading, (state, action) => ({
        ...state,
        [action.payload.viewType]: {
            ...state[action.payload.viewType],
            listLoading: action.payload.loading
        }
    })),

    on(ApplicationsActions.SetItemsLoading, (state, action) => ({
        ...state,
        [action.payload.viewType]: {
            ...state[action.payload.viewType],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state[action.payload.viewType].itemsLoading, action.payload.id, action.payload.isStart)
        }
    })),

    on(ApplicationsActions.AddItemSuccess, (state, action) => {
        const savedItemAvailableByFilterOnBoard = isAvailableByFilter(state['BOARD'].filter, action.payload.item);
        const savedItemAvailableByFilterInList = isAvailableByFilter(state['LIST'].filter, action.payload.item);

        return {
            ...state,
            BOARD: savedItemAvailableByFilterOnBoard ? adapter.setOne(action.payload.item, {
                ...state['BOARD'],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state['BOARD'].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state['BOARD'].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state['BOARD'].pagination, state['BOARD']?.ids)
                }
            }) : state['BOARD'],
            LIST: savedItemAvailableByFilterInList ? adapter.setOne(action.payload.item, {
                ...state['LIST'],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state['LIST'].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state['LIST'].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state['LIST'].pagination, state['LIST']?.ids)
                }
            }) : state['LIST']
        };
    }),

    on(ApplicationsActions.UpdateItemSuccess, (state, action) => {
        const savedItemAvailableByFilterOnBoard = isAvailableByFilter(state['BOARD'].filter, action.payload.item);
        const savedItemAvailableByFilterInList = isAvailableByFilter(state['LIST'].filter, action.payload.item);

        return {
            ...state,
            BOARD: savedItemAvailableByFilterOnBoard
                // ? adapter.updateOne({id: action.payload.item!.id!, changes: action.payload.item}, {
                ? adapter.setOne(action.payload.item, {
                    ...state['BOARD'],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state['BOARD'].itemsLoading, action.payload.item.id!, false),
                    pagination: {...state['BOARD'].pagination}
                })
                : adapter.removeOne(action.payload.item.id!, {
                    ...state['BOARD'],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state['BOARD'].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state['BOARD'].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state['BOARD'].pagination, state['BOARD']?.ids, true)
                    }
                }),
            LIST: savedItemAvailableByFilterInList
                // ? adapter.updateOne({id: action.payload.item!.id!, changes: action.payload.item}, {
                ? adapter.setOne(action.payload.item, {
                    ...state['LIST'],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state['LIST'].itemsLoading, action.payload.item.id!, false),
                    pagination: {...state['LIST'].pagination}
                })
                : adapter.removeOne(action.payload.item.id!, {
                    ...state['LIST'],
                    itemsLoading: StoredComponentHelperService.updateItemLoading(state['LIST'].itemsLoading, action.payload.item.id!, false),
                    pagination: {
                        ...state['LIST'].pagination,
                        totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state['LIST'].pagination, state['LIST']?.ids, true)
                    }
                })
        };
    }),

    on(ApplicationsActions.DeleteItem, (state, action) => ({
        ...state,
        LIST: {
            ...state['LIST'],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state['LIST'].itemsLoading, action.payload.item.id!, true)
        },

        BOARD: {
            ...state['BOARD'],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state['BOARD'].itemsLoading, action.payload.item.id!, true)
        }
    })),

    on(ApplicationsActions.DeleteItemSuccess, (state, action) => {
        return ({
            ...state,
            LIST: adapter.removeOne(action.payload.item.id!, {
                ...state['LIST'],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state['LIST'].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state['LIST'].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state['LIST'].pagination, state['LIST']?.ids, true)
                }
            }),

            BOARD: adapter.removeOne(action.payload.item.id!, {
                ...state['BOARD'],
                itemsLoading: StoredComponentHelperService.updateItemLoading(state['BOARD'].itemsLoading, action.payload.item.id!, false),
                pagination: {
                    ...state['BOARD'].pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.item.id!, state['BOARD'].pagination, state['BOARD']?.ids, true)
                }
            })
        });
    }),

    on(ApplicationsActions.DeleteItemFailure, (state, action) => ({
        ...state,
        LIST: {
            ...state['LIST'],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state['LIST'].itemsLoading, action.payload.item.id!, false)
        },

        BOARD: {
            ...state['BOARD'],
            itemsLoading: StoredComponentHelperService.updateItemLoading(state['BOARD'].itemsLoading, action.payload.item.id!, false)
        }
    })),


    // <editor-fold desc="Comments">

    on(ApplicationsActions.LoadComments, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                pagination: {
                    ...state.openedItem.comments.pagination,
                    number: action.payload.next ? StoredComponentHelperService.setNextPage(state.openedItem.comments.pagination, state.openedItem.comments.ids.length) : 0
                },
                listLoading: action.payload.next ? 'listLoadNext' as DahLoadingType : 'listLoading' as DahLoadingType
            }
        }
    })),

    on(ApplicationsActions.LoadCommentsSuccess, (state, action) => {
        return {
            ...state,
            openedItem: {
                ...state.openedItem,
                comments: action.payload?.next
                    ? commentsAdapter.upsertMany(action.payload.comments, {
                        ...state.openedItem.comments,
                        pagination: action.payload.pagination,
                        listLoading: undefined
                    })
                    : commentsAdapter.setAll(action.payload.comments, {
                        ...state.openedItem.comments,
                        pagination: action.payload.pagination,
                        listLoading: undefined
                    })
            }
        };
    }),

    on(ApplicationsActions.LoadCommentsFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: undefined
            }
        }
    })),

    on(ApplicationsActions.SendComment, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: 'entityUpdating' as DahLoadingType
            }
        }
    })),

    on(ApplicationsActions.EditComment, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.itemId, true),
                listLoading: 'entityUpdating' as DahLoadingType
            }
        }
    })),

    on(ApplicationsActions.SendCommentSuccess, (state, action) => ({
        ...state,

        openedItem: {
            comments: commentsAdapter.addOne(action.payload.item, {
                ...state.openedItem.comments,
                listLoading: undefined
            })
        }
    })),

    on(ApplicationsActions.EditCommentSuccess, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: commentsAdapter.setOne(action.payload.item, {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.item.id, false),
                listLoading: undefined
            })
        }
    })),

    on(ApplicationsActions.DeleteComment, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.itemId, true),
                listLoading: 'entityUpdating' as DahLoadingType
            }
        }
    })),

    on(ApplicationsActions.DeleteCommentSuccess, (state, action) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: commentsAdapter.removeOne(action.payload.itemId, {
                ...state.openedItem.comments,
                itemsLoading: StoredComponentHelperService.updateItemLoading(state.openedItem.comments.itemsLoading, action.payload.itemId, false),
                listLoading: undefined,

                pagination: {
                    ...state.openedItem.comments.pagination,
                    totalElements: StoredComponentHelperService.setTotalElements(action.payload.itemId, state.openedItem.comments.pagination, state.openedItem.comments.ids, true)
                }
            })
        }
    })),

    on(ApplicationsActions.SendCommentFailure, CustomerServiceActions.EditCommentFailure, CustomerServiceActions.DeleteCommentFailure, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {
                ...state.openedItem.comments,
                listLoading: undefined
            }
        }
    })),

    on(ApplicationsActions.SetInitialCommentsState, (state) => ({
        ...state,
        openedItem: {
            ...state.openedItem,
            comments: {...commentsInitialState}
        }
    })),

    // <editor-fold>

    on(ApplicationsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const selectAllEntities = (type: ApplicationsViewType) => adapter.getSelectors((state: ApplicationsState) => state[type]).selectAll;

const isAvailableByFilter = (searchParams: ApplicationsSearchParams | undefined, entity: ApplicationModel): boolean => {
    if (!searchParams) {
        return true;
    }

    if (searchParams.from && dayjs(searchParams.from).isAfter(dayjs(entity.created))) {
        return false;
    }

    if (searchParams.to && dayjs(entity.created).isAfter(dayjs(searchParams.to))) {
        return false;
    }

    if (searchParams.archivedStatus != undefined && entity.archived != searchParams.archivedStatus) {
        return false;
    }

    if (searchParams.categoryHash) {
        const categoryHash = Md5.hashStr(entity.categoryName! + entity.categoryWorkType);

        if (categoryHash != searchParams.categoryHash) {
            return false;
        }
    }

    if (searchParams.statuses && !searchParams.statuses?.find(el => el == entity.status)) {
        return false;
    }

    return !(searchParams.responsible?.id && searchParams.responsible.id != entity.responsible?.userId);
};
